import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'

import PageHeader from '../../components/system/PageHeader'
import { BRAND_GRADIENT } from '../../components/social-media/design-tokens'
import { HeroStat, SectionHeader, StatStrip } from '../../components/socialdash'
import { formatTokens } from '../../components/llmusage/llmUsageModel'
import { FEATURE_LABELS } from '../../types/tokenUsage'
import type { TokenUsageFeatureKey } from '../../types/tokenUsage'
import { USAGE_WINDOWS } from '../../types/llmUsage'
import type { LlmUsageDimension } from '../../types/llmUsage'
import useRouter from '../../hooks/useRouter'
import { useProductSpend, useSkyProducts } from '../../hooks/socialdash'
import { orderProductTabs } from './ProductTabs'

/** `USAGE_WINDOWS` key -> a `startTime` in ms, or undefined for "all time". */
const windowStart = (key: string, now: number = Date.now()): number | undefined => {
  const w = USAGE_WINDOWS.find((x) => x.key === key)
  if (!w || w.hours === null) return undefined
  return now - w.hours * 60 * 60 * 1000
}

const featureLabel = (key: string): string => FEATURE_LABELS[key as TokenUsageFeatureKey] ?? key

/**
 * `/sky-social-media/:product/spend` — one department's own token spend.
 *
 * NO PER-CAMPAIGN BREAKDOWN. The design doc's T17 ticket describes "a
 * per-campaign breakdown", but the T16 endpoint this page consumes
 * (`goapi/pkg/server/sky_spend.go`) deliberately carries no campaign
 * dimension at all — `SkyProductSpend` drops the summary's `ByLabel` grouping
 * before it reaches JSON, and `TestGetSkyProductSpend_DoesNotEchoCampaignIDs`
 * pins that a product's campaign ids must never appear in this response. That
 * matches the sibling design doc's own rule for the chooser and dashboards
 * (`design/2026-08-11-sky-product-dashboards.md`: "Neither campaign nor
 * product is ever offered to Sky as a UI filter"). So this page shows the
 * breakdowns the response actually carries — by feature and by model — rather
 * than inventing a campaign breakdown the server will not send. Logged as a
 * Discovered Issue against T17 rather than resolved unilaterally.
 *
 * Presentational + one data hook, same division as the five dashboards: this
 * file arranges, `useProductSpend` fetches, the `socialdash` kit renders the
 * primitives it shares with them.
 */
export const ProductSpend: React.FC = () => {
  const router = useRouter()
  const slug = String(router.params.product || '').trim().toLowerCase()

  const { products, isLoading: productsLoading } = useSkyProducts()
  const product = products.find((p) => p.slug === slug)
  // Same ordering the dashboard tabs use, so "All products" is last in both.
  const productTabs = orderProductTabs(products)

  const [windowKey, setWindowKey] = useState('30d')
  const startTime = windowStart(windowKey)

  const { spend, isLoading, error, refetch } = useProductSpend(slug, { startTime }, Boolean(slug))

  // Same "unknown slug is a flat message, not a redirect" rule as
  // SkyProductRoute — a bookmarked or mistyped URL should say so.
  if (!productsLoading && !product) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">
          There is no department called “{slug || 'unknown'}”. Pick one from the Social Media page.
        </Alert>
      </Container>
    )
  }

  const title = product ? `${product.name} — Token Spend` : 'Token Spend'
  const periodLabel = USAGE_WINDOWS.find((w) => w.key === windowKey)?.label ?? windowKey

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', bgcolor: 'background.default' }}>
      <PageHeader
        title={title}
        gradient={BRAND_GRADIENT}
        subtitle="Model tokens spent coding this department's comments. Tokens only — no cost is shown here or anywhere on this page."
        actions={
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* SWITCHING DEPARTMENT WITHOUT LEAVING THE PAGE (Matt, 2026-08-17).
                The dashboards carry a tab strip; this page deliberately does
                NOT — token spend is a different question about a department,
                not a seventh dashboard, so it sits outside the tab set and
                changes department with its own control.

                It lists the SAME audience-filtered catalogue the tabs use, so a
                non-admin never sees "All products" in either place. Navigating
                re-enters this route with a new :product, which re-runs the
                spend query — there is no local state to keep in step. */}
            {productTabs.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="sky-spend-product-label">Department</InputLabel>
                <Select
                  labelId="sky-spend-product-label"
                  label="Department"
                  value={productTabs.some((p) => p.slug === slug) ? slug : ''}
                  onChange={(e) => router.navigate('sky-social-media-product-spend', { product: e.target.value })}
                  data-testid="sky-spend-product-select"
                >
                  {productTabs.map((p) => (
                    <MenuItem key={p.slug} value={p.slug}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sky-spend-period-label">Period</InputLabel>
              <Select
                labelId="sky-spend-period-label"
                label="Period"
                value={windowKey}
                onChange={(e) => setWindowKey(e.target.value)}
                data-testid="sky-spend-period-select"
              >
                {USAGE_WINDOWS.map((w) => (
                  <MenuItem key={w.key} value={w.key}>
                    {w.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              color="info"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refetch}
              disabled={isLoading}
              data-testid="sky-spend-refresh"
            >
              Refresh
            </Button>
            <Button
              color="info"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => router.navigate('sky-social-media-product', { product: slug })}
            >
              Back to dashboard
            </Button>
          </Stack>
        }
      />

      {/* An error must read as an error, never as a silent zero. No stat tile,
          no "0 tokens" — just the message and a way to retry. */}
      {error ? (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert
            severity="error"
            data-testid="sky-spend-error"
            action={
              <Button color="info" onClick={refetch}>
                Retry
              </Button>
            }
          >
            Could not load spend for this department: {error.message}
          </Alert>
        </Container>
      ) : isLoading && !spend ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress color="info" />
        </Box>
      ) : (
        <>
          <SectionHeader title="Total Spend" subtitle={periodLabel} />
          <StatStrip data-testid="sky-spend-kpi-strip">
            <HeroStat
              label="Total Tokens"
              value={formatTokens(spend?.total_tokens)}
              caption="input + output"
              data-testid="sky-spend-total-tokens"
            />
            <HeroStat
              label="Input Tokens"
              value={formatTokens(spend?.total_input_tokens)}
              caption="sent to the model"
            />
            <HeroStat
              label="Output Tokens"
              value={formatTokens(spend?.total_output_tokens)}
              caption="returned by the model"
            />
            <HeroStat
              label="Cache Read"
              value={formatTokens(spend?.total_cached_tokens)}
              caption="tokens served from cache"
            />
            <HeroStat
              label="Requests"
              value={(spend?.request_count ?? 0).toLocaleString()}
              caption="model calls"
            />
          </StatStrip>

          <SectionHeader title="By Feature" subtitle="Which workflows used these tokens" />
          <DimensionTable
            rows={spend?.by_feature}
            displayName={featureLabel}
            emptyLabel="No feature spend in this period."
            testId="sky-spend-by-feature"
          />

          <SectionHeader title="By Model" subtitle="Which models were called" />
          <DimensionTable
            rows={spend?.by_model}
            displayName={(d) => d}
            emptyLabel="No model spend in this period."
            testId="sky-spend-by-model"
          />
        </>
      )}
    </Box>
  )
}

const DimensionTable: React.FC<{
  rows: LlmUsageDimension[] | undefined
  displayName: (dimension: string) => string
  emptyLabel: string
  testId: string
}> = ({ rows, displayName, emptyLabel, testId }) => {
  const list = rows ?? []
  const total = list.reduce((sum, r) => sum + r.total_tokens, 0)

  if (!list.length) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      data-testid={testId}
      sx={{ mx: 3, mb: 3, mt: 1, border: '1px solid', borderColor: 'divider', width: 'auto' }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align="right">Requests</TableCell>
            <TableCell align="right">Tokens</TableCell>
            <TableCell align="right">Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((row) => (
            <TableRow key={row.dimension || '—'} hover>
              <TableCell>{displayName(row.dimension) || '—'}</TableCell>
              <TableCell align="right">{row.request_count.toLocaleString()}</TableCell>
              <TableCell align="right">{formatTokens(row.total_tokens)}</TableCell>
              <TableCell align="right">
                {total > 0 ? `${((row.total_tokens / total) * 100).toFixed(1)}%` : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ProductSpend
